import React, { FC, useContext, useEffect, useState } from "react";
import { isEmpty, isNumber } from "lodash";
import { useFormContext } from "react-hook-form";
import { SUBMISSION_TYPE_MODULE } from "common/constants";
import { NODE_ENV } from "common/env";
import { convertJSONSchemaObjectToFormStructure } from "common/services/formBuilderService";
import { AuthContext } from "../../../../../Authorization/AuthContext";
import { BANNER_TYPES } from "../../../../../Common/__styles__/Banner";
import Banner from "../../../../../Common/Banner";
import { Button } from "../../../../../Common/Button";
import { Icon } from "../../../../../Common/Icons/LucideIcons";
import { Body } from "../../../../../Common/Typography";
import { BuilderSidebarHeader } from "../__styles__/SubmissionsBuilder";
import {
  createAccountDocumentTypeOptions,
  inputTypeToFieldsListLabelMap,
  MODULES,
  SubmissionsBuilderFieldType,
} from "../constants";
import { SubmissionsBuilderContextInstance } from "../context";
import { SupportedMobileVersionsContextInstance } from "../contexts/supportedMobileVersionsContext";
import { SelectedSubmissionsBuilderField } from "../reducer";
import {
  SubmissionsBuilderField,
  SubmissionsBuilderFieldSettings,
  SubmissionsBuilderFormDataStructure,
} from "../types";
import { getAllInputs } from "../utils";
import { SupportedMobileVersionsBannerWrapper } from "./__styles__/FieldSettingsView";
import CheckboxWidgetSettings from "./FieldSettings/CheckboxWidgetSettings";
import DamageDegreeWidgetSettings from "./FieldSettings/DamageDegreeWidgetSettings";
import DateWidgetSettings from "./FieldSettings/DateWidgetSettings";
import DocumentUploadFieldSettings from "./FieldSettings/DocumentUploadFieldSettings";
import DropdownWidgetSettings from "./FieldSettings/DropdownWidgetSettings";
import MultiSelectWidgetSettings from "./FieldSettings/MultiSelectWidgetSettings";
import NumberWidgetSettings from "./FieldSettings/NumberFieldSettings";
import NumberWithUnitsFieldSettings from "./FieldSettings/NumberWithUnitsFieldSettings";
import ObjectFieldTemplateSettings from "./FieldSettings/ObjectFieldTemplateSettings";
import PropertyMarketFieldSettings from "./FieldSettings/PropertyMarketValueFieldSettings";
import TagsWidgetSettings from "./FieldSettings/TagsWidgetSettings";
import TextareaWidgetSettings from "./FieldSettings/TextareaWidgetSettings";
import TextWidgetSettings from "./FieldSettings/TextWidgetSettings";
import UniqueIdFieldSettings from "./FieldSettings/UniqueIdFieldSettings";
import UserWidgetSettings from "./FieldSettings/UserWidgetSettings";

const fieldKeyToSettingsFormMap: Record<
  SubmissionsBuilderFieldType,
  FC<SelectedSubmissionsBuilderField & SubmissionsBuilderFieldSettings>
> = {
  checkbox: CheckboxWidgetSettings,
  DamageDegree: DamageDegreeWidgetSettings,
  date: DateWidgetSettings,
  DocumentUploader: DocumentUploadFieldSettings,
  group: ObjectFieldTemplateSettings,
  multiSelect: MultiSelectWidgetSettings,
  number: NumberWidgetSettings,
  NumberWithUnits: NumberWithUnitsFieldSettings,
  PropertyMarketValue: PropertyMarketFieldSettings,
  select: DropdownWidgetSettings,
  Tags: TagsWidgetSettings,
  text: TextWidgetSettings,
  textarea: TextareaWidgetSettings,
  User: UserWidgetSettings,
  UniqueId: UniqueIdFieldSettings,
};

const SupportedMobileVersionsBanner = ({
  lastUnsupportedMobileVersion,
}: {
  lastUnsupportedMobileVersion: Maybe<string>;
}) => {
  const message =
    !isEmpty(lastUnsupportedMobileVersion) ?
      `This field is not available in mobile versions ${lastUnsupportedMobileVersion} and below, which may affect the accessibility of the form in the app.`
    : "This field is not currently available in the mobile app but we are working on adding it in a future update.";

  return (
    <SupportedMobileVersionsBannerWrapper>
      <Banner type={BANNER_TYPES.ATTENTION} items={[message]} />
    </SupportedMobileVersionsBannerWrapper>
  );
};

const FieldSettingsView = ({ readOnly }: { readOnly: boolean }) => {
  const { account } = useContext(AuthContext);
  const accountDocumentTypeOptions = createAccountDocumentTypeOptions(account);

  const { watch, getValues } =
    useFormContext<SubmissionsBuilderFormDataStructure>();
  const supportedMobileVersionsMetaByFieldType = useContext(
    SupportedMobileVersionsContextInstance,
  );
  const { selectedField, toggleFormSettingsMode } = useContext(
    SubmissionsBuilderContextInstance,
  );

  const [associatedModules, setAssociatedModules] = useState<
    Array<SUBMISSION_TYPE_MODULE>
  >([]);

  const modules = watch("modules");
  useEffect(() => {
    setAssociatedModules(
      MODULES.filter(module => modules.includes(module.type))
        .filter(module => {
          const formStructure = module.getFormStructure(
            accountDocumentTypeOptions,
          );
          const inputs = convertJSONSchemaObjectToFormStructure(formStructure);

          const nestedInputs = getAllInputs([...inputs]);
          return nestedInputs.some(
            input => input.name === selectedField?.name && input.required,
          );
        })
        .map(module => module.type),
    );
  }, [modules, selectedField?.name]);

  if (!selectedField) {
    return null;
  }

  const inputs = getValues("inputs") ?? [];

  const indexes: Array<number> = selectedField.fieldPath
    .split(".")
    .filter(part => isNumber(Number(part)))
    .map(part => Number(part));

  const names: Array<string> = [];

  let parentInput: Array<SubmissionsBuilderField> | undefined = inputs;
  indexes.forEach(index => {
    const inputAtIndex = parentInput?.[index];
    if (!inputAtIndex) {
      return;
    }
    names.push(inputAtIndex.name);
    parentInput = inputAtIndex.properties;
  });

  const formDataPath = `formData.${names.join(".")}`;

  const FieldSettingsForm = fieldKeyToSettingsFormMap[selectedField.inputType];
  const title =
    selectedField.inputType === "DamageDegree" ?
      "Damage degree"
    : inputTypeToFieldsListLabelMap[selectedField.inputType];

  const { possiblyUnsupported, lastUnsupportedMobileVersion } =
    supportedMobileVersionsMetaByFieldType[selectedField.inputType];

  // when a field is selected, the side column changes its content to show the field settings
  // when you're in field mode, you can navigate BACK to selected fields
  return (
    <>
      <BuilderSidebarHeader role="none">
        <Button
          aria-label="Back to form fields"
          style={{ padding: 0 }}
          styleVariant="ghost"
          onClick={toggleFormSettingsMode}
        >
          <Icon color="contentSecondary" iconName="chevron-left" size={16} />
        </Button>
        <Body size="large" type="emphasis">
          {`${title} field properties`}
        </Body>
        {NODE_ENV === "development" && (
          <Icon
            color="contentSecondary"
            iconName="copy"
            size={16}
            onClick={async () => {
              await navigator.clipboard.writeText(formDataPath);
            }}
          />
        )}
      </BuilderSidebarHeader>
      {possiblyUnsupported && (
        <SupportedMobileVersionsBanner
          lastUnsupportedMobileVersion={lastUnsupportedMobileVersion}
        />
      )}
      {/* adding `key` prop here to force re-mounting of component on selectedField change */}
      <FieldSettingsForm
        key={selectedField.name}
        {...selectedField}
        isModuleInput={!!associatedModules.length}
        readOnly={readOnly}
      />
    </>
  );
};

export default FieldSettingsView;
